import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import AdminDashboard from "../Layout/AdminDashboard";
import moment from "moment";
import { Spin, Button } from "antd";
import UpdateGenerateProposalModal from "./UpdateGenrateProposalModal";
import LetterHeadTop from "../../assets/LetterHeadTop.svg";
import LetterHeadBottom from "../../assets/LetterHeadBottom.svg";

import "../css/view.css";
import toast from "react-hot-toast";

const ViewProposal = () => {
  const { proposalId } = useParams(); // Extract proposalId from the route
  const [proposalData, setProposalData] = useState(null);
  const [zoom, setZoom] = useState(1); // State to manage zoom level
  const [noteContent, setNoteContent] = useState([
    "1. This proposal is valid for 30 days from the date of receipt.",
    "2. 100% advance payment to be done for the project.",
    "3. 18% Taxes as applicable in state.",
  ]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const [isAddressValid, setIsAddressValid] = useState(true);
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  const fetchProposalData = async () => {
    try {
      // Fetch proposal, company, and bank details concurrently
      const [proposalResponse, companyResponse, bankDetailsResponse] =
        await Promise.all([
          axios.get(`${apiUrl}/api/proposal/getProposalById/${proposalId}`),
          axios.get(`${apiUrl}/api/setting/getCompanyDetail`),
          axios.get(`${apiUrl}/api/setting/getTheBankDetails`),
        ]);

      const proposalData = proposalResponse.data;
      const companyProfile = companyResponse.data?.profile; // Extract profile from company response
      const bankDetails = bankDetailsResponse.data?.bankDetail; // Extract bankDetail from bank details response
      // console.log(proposalData, "proposal Data");
      // console.log(companyProfile, "company Profile");
      // console.log(bankDetails, "bank Details");

      const line2 = proposalData?.address?.line2?.trim();

      // Check whether Address Line 2 is valid
      const validAddress =
        line2 &&
        line2.includes(",") &&
        line2.split(",")[1]?.trim();

      setIsAddressValid(!!validAddress);

      if (!validAddress) {
        toast.error(
          "Please complete the client address (City, State) in the Customer Form before viewing the proposal."
        );
      }
      setProposalData(proposalData);
      setCompanyProfile(companyProfile);
      setBankDetails(bankDetails);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    const fetchNoteContent = async () => {
      try {
        const response = await axios.get(`${apiUrl}/api/setting/getSetting`);
        const formattedNote = response.data.proposal_note
          ? response.data.proposal_note.split("\n")
          : noteContent;
        setNoteContent(formattedNote);
      } catch (error) {
        console.error("Error fetching note content:", error);
      }
    };

    fetchProposalData();
    fetchNoteContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  if (!proposalData) {
    return (
      <AdminDashboard>
        <div className="flex justify-center items-center h-screen">
          <Spin />
        </div>
      </AdminDashboard>
    );
  }

  const formattedDate = moment(proposalData.proposal_date).format(
    "MMMM D, YYYY"
  );

  // Determine whether we need Production Line column
  const hasManufacturing = Array.isArray(proposalData.outlets)
    ? proposalData.outlets.some(
      (o) => (o.type_of_industry || "").toString() === "Manufacturing"
    )
    : false;

  // Build outlet rows conditionally
  const outletRows = (proposalData.outlets || [])
    .map((outlet) => {
      let postfix = "";
      switch (outlet.type_of_industry) {
        case "Transportation":
          postfix = "VH";
          break;
        case "Catering":
          postfix = "FH";
          break;
        case "Trade and Retail":
        case "Storage and Warehouse":
          postfix = "Sq ft";
          break;
        case "Manufacturing":
          postfix = "FH";
          break;
        default:
          postfix = ""; // or default
      }

      const outletName = outlet.outlet_name || "";
      let service = outlet.unit ? `${outlet.unit} ${postfix}` : "";
      // handle mongoose Decimal128 / number wrappers safely
      const manDays =
        outlet.man_days?.$numberDouble ?? outlet.man_days ?? 0;
      const quantity =
        outlet.quantity?.$numberInt ?? outlet.quantity ?? 0;
      const unitCost =
        outlet.unit_cost?.$numberInt ?? outlet.unit_cost ?? 0;
      const amount = outlet.amount?.$numberInt ?? outlet.amount ?? 0;
      const type_of_industry = outlet.type_of_industry || "";
      const vertical_of_industry =
        outlet.vertical_of_industry || proposalData.vertical_of_industry || "";

      if (outletName === "Others") {
        service = "N/A";
      }

      // Build HTML row with conditional Production Line cell
      const productionCell = hasManufacturing
        ? `<td class="px-2 py-1 text-center">${outlet.no_of_production_line ?? ""}</td>`
        : "";

      const extraText =
        outlet.outlet_name !== "Others" && vertical_of_industry
          ? ` (${vertical_of_industry})`
          : "";

      return `
      <tr>
        <td class="px-2 py-1 text-center">${`${outletName}${outlet.city ? ` - ${outlet.city}` : ""
        }`}</td>
        <td class="px-2 py-1 text-center">${`${outlet.description || ""}${extraText}`}</td>
        <td class="px-2 py-1 text-center">${service}</td>
        ${productionCell}
        <td class="px-2 py-1 text-center">${manDays}</td>
        <td class="px-2 py-1 text-center">${quantity}</td>
        <td class="px-2 py-1 text-center">${unitCost.toFixed(2)}</td>
        <td class="px-2 py-1 text-center">${amount.toFixed(2)}</td>
      </tr>
    `;
    })
    .join("");

  // Auditor Fee Row
  const formatCurrency = (value) => {
    if (value === "N/A" || value === undefined || value === null) return "0.00";
    return Number(value).toFixed(2);
  };
  const numericAuditorFee = proposalData.auditor_convenience_fee ?? 0;
  const auditorFee = formatCurrency(numericAuditorFee);

  const productionCellFinanceRow = hasManufacturing
    ? `<td class="px-2 py-1 text-center">--</td>`
    : "";
  const financialRows =
    numericAuditorFee !== 0
      ? ` <tr>
         <td class="px-2 py-1 text-center">Others</td>
         <td class="px-2 py-1 text-center">Auditor Conveyance Charges</td>
         <td class="px-2 py-1 text-center">--</td>
         <td class="px-2 py-1 text-center">--</td>
         <td class="px-2 py-1 text-center">--</td>
         <td class="px-2 py-1 text-center">--</td>
         ${productionCellFinanceRow}
         <td class="px-2 py-1 text-center currency-cell">${auditorFee}</td>
       </tr>`
      : ``;
  // compute total
  let total = 0;
  (proposalData.outlets || []).forEach((outlet) => {
    total += parseFloat(
      outlet.amount?.$numberInt ?? outlet.amount ?? 0
    );
  });

  const beforeTaxTotal = total + parseFloat(auditorFee);

  const roundUpRupee = (value) => {
    if (!value) return 0;
    return Math.ceil(Number(value));
  };

  // Tax calculations
  let cgst = 0;
  let sgst = 0;
  let igst = 0;
  let overallTotal = 0;

  if (proposalData.same_state) {
    cgst = parseFloat((beforeTaxTotal * 0.09).toFixed(2)); // 9% CGST
    sgst = parseFloat((beforeTaxTotal * 0.09).toFixed(2)); // 9% SGST

    overallTotal = roundUpRupee(parseFloat((beforeTaxTotal + cgst + sgst).toFixed(2)));
  } else {
    // igst = parseFloat((beforeTaxTotal * 0.18).toFixed(2)); // 18% GST
    // overallTotal = roundUpRupee(parseFloat((beforeTaxTotal + igst).toFixed(2)));
    igst = parseFloat((beforeTaxTotal * 0.18).toFixed(2)); // 18% GST
    // sgst = parseFloat((beforeTaxTotal * 0.09).toFixed(2)); // 9% GST
    overallTotal = roundUpRupee(parseFloat((beforeTaxTotal + igst + sgst).toFixed(2)));
  }

  // columns count (used for colspan)
  const columnsCount = hasManufacturing ? 8 : 7;
  const labelColspan = columnsCount - 1; // label spans all but amount column

  // Tax row HTML
  const taxHtml = proposalData.same_state
    ? `
    <tr>
      <td colspan="${labelColspan}" class="border text-right w-3/4 small-cell">
        <strong>CGST [9%]</strong>
      </td>
      <td class="border w-1/4 small-cell text-center"><strong>₹${cgst.toFixed(2)}</strong></td>
    </tr>
    <tr>
      <td colspan="${labelColspan}" class="border text-right w-3/4 small-cell">
        <strong>SGST [9%]</strong>
      </td>
      <td class="border w-1/4 small-cell text-center"><strong>₹${sgst.toFixed(2)}</strong></td>
    </tr>
    <tr>
      <td colspan="${labelColspan}" class="border text-right w-3/4 small-cell">
        <strong>IGST</strong>
      </td>
      <td class="border w-1/4 small-cell text-center"><strong>₹${igst.toFixed(2)}</strong></td>
    </tr>
  `
    : `
   <tr>
      <td colspan="${labelColspan}" class="border text-right w-3/4 small-cell">
        <strong>IGST [18%]</strong>
      </td>
      <td class="border w-1/4 small-cell text-center"><strong>₹${igst.toFixed(2)}</strong></td>
    </tr>
    <tr>
      <td colspan="${labelColspan}" class="border text-right w-3/4 small-cell">
        <strong>SGST </strong>
      </td>
      <td class="border w-1/4 small-cell text-center"><strong>₹${sgst.toFixed(2)}</strong></td>
    </tr>
    <tr>
      <td colspan="${labelColspan}" class="border text-right w-3/4 small-cell">
        <strong>CGST </strong>
      </td>
      <td class="border w-1/4 small-cell text-center"><strong>₹${cgst.toFixed(2)}</strong></td>
    </tr>
  `;

  const tax2 = proposalData.same_state
    ? `  <tr>
                <td class="w-1/2 border px-4 py-1"><strong>CGST [9%]</strong></td>
                <td class="w-1/2 border px-4 py-1"><strong class="font-medium" >₹${cgst.toFixed(2)}</strong></td>
              </tr>
              <tr>
                <td class="w-1/2 border px-4 py-1"><strong>SGST [9%]</strong></td>
                <td class="w-1/2 border px-4 py-1"><strong  class="font-medium">₹${sgst.toFixed(2)}</strong></td>
              </tr>
              <tr>
                <td class="w-1/2 border px-4 py-1"><strong>IGST</strong></td>
                <td class="w-1/2 border px-4 py-1"><strong  class="font-medium">₹${igst.toFixed(2)}</strong></td>
              </tr>
              
              `
    : ` <tr>
                <td class="w-1/2 border px-4 py-1"><strong>IGST [18%]</strong></td>
                <td class="w-1/2 border px-4 py-1"><strong class="font-medium" >₹${igst.toFixed(2)}</strong></td>
              </tr>
              <tr>
                <td class="w-1/2 border px-4 py-1"><strong>SGST </strong></td>
                <td class="w-1/2 border px-4 py-1"><strong  class="font-medium">₹${sgst.toFixed(2)}</strong></td>
              </tr>
              
              <tr>
                <td class="w-1/2 border px-4 py-1"><strong>CGST </strong></td>
                <td class="w-1/2 border px-4 py-1"><strong  class="font-medium">₹${cgst.toFixed(2)}</strong></td>
              </tr>
              <tr> `;

  const showModal = () => {
    const line2 = proposalData?.address?.line2?.trim();

    if (!isAddressValid) {
      toast.error(
        "Please complete the client address (City, State) in the Customer Form before editing the proposal."
      );
      return;
    }


    setIsModalVisible(true);
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  const handleCancel = () => {
    fetchProposalData();
    setIsModalVisible(false);
  };

  // Download handler with api call get file
  const handleDownload = async () => {
    if (downloading) return;

    setDownloading(true);
    const toastId = toast.loading("Please wait, PDF downloading...");
    try {
      const response = await axios.post(
        `${apiUrl}/api/proposal/generateProposal/${proposalId}?download=true`,
        {},
        {
          responseType: "blob",
        }
      );

      // Create blob URL
      const blob = new Blob([response.data], {
        type: "application/pdf",
      });
      // wait for loading 

      const url = window.URL.createObjectURL(blob);

      // Create temp anchor
      const link = document.createElement("a");
      link.href = url;
      link.download = `Proposal_${proposalData?.proposal_number || proposalId}_${proposalData?.fbo_name}.pdf`;

      document.body.appendChild(link);
      link.click();

      // Cleanup
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded successfully", { id: toastId });
    } catch (error) {
      console.error(error);
      toast.error("Failed to download PDF", { id: toastId });
    } finally {
      setDownloading(false);
    }
  };


  console.log(proposalData.note, "proposalData.note");

  // Build the header HTML dynamically
  const productionHeader = hasManufacturing
    ? `<th class="px-2 py-1 text-center">Production Line</th>`
    : "";

  // Inject data into HTML template (header uses productionHeader and subtotal uses labelColspan)
  const htmlTemplate = `
    <!DOCTYPE html>
    <html lang="en">
    <head>
      <meta charset="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <title>Quotation</title>
      <!-- Tailwind CSS -->
      <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet" />
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css" integrity="sha512-SnH5WK+bZxgPHs44uWIX+LLJAJ9/2PkPKZ5QiAj6Ta86w+fsb2TkcmfRyVX3pBnMFcV7oQPJkl9QevSCWr3W6A==" crossorigin="anonymous" referrerpolicy="no-referrer" />
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        @page {
          size: A4;
          margin: 0;
        }
        html {
          -webkit-print-color-adjust: exact;
        }
        .container {
          width: 793px; /* A4 width in pixels */
          margin: 0 auto; /* Center horizontally */
          transform: scale(${zoom}); /* Adjust scale for zoom effect */
          transform-origin: top left;
          position: relative; /* Allow centering with transform */
        }

         .content-box {
            position: relative;
            background-image:
                linear-gradient(rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.9)),
                url('/logo2.png');
          
            background-repeat: no-repeat;
            background-size:  70% auto;
            background-position: center center;
        }
        .a4 {
          border: 2px solid black; /* Add border to a4 class */
        }
        .a4 img {
          width: 100%;
          height: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th,
        td {
          border: 1px solid black;
          padding: 6px; /* Adjust padding for a more professional look */
          text-align: left;
          font-size: 12px; /* Adjust font size */
        }
        .selected-cell {
          background-color: rgba(242, 242, 242, 0.5) !important;
        }
        .small-cell {
          padding: 4px; /* Adjust padding */
        }
        .page-break-before {
          page-break-before: always;
        }
      </style>
    </head>
    <body class="bg-white text-xs relative">
    <div class=" absolute top-0 right-0 left-[60%]  z-10"><img src=${LetterHeadTop} /></div>
      <div class="container bg-white rounded-lg ">
        <div class="flex py-2 header items-center mb-4">
          <div class="w-[80px]">
            <img src="/logo2.png" alt="Your Image Alt Text" width="80" height="80" />
          </div>
          <div>
            <h1 class=" font-bold text-[1.5rem] text-gray-800">
              Unavar Food Inspection and Certification Private Limited
            </h1>
          </div>
        </div>
        <div>
        <div class="content-box">
        <div class="mt-5 mx-4">
          <table id="clientProposalTable" class="w-full">
            <tr>
              <td colspan="2">
                <strong>To</strong><br />
                <span class="font-medium">Client Name: </span>${proposalData.fbo_name
    }<br />
                <span class="font-medium">Client Mail ID: </span>${proposalData.email
    }<br />
                <span class="font-medium">Contact Person Name: </span>${proposalData.contact_person
    }<br />
                <span class="font-medium">Contact Person Number: </span>${proposalData.phone
    }<br />
                <span class="font-medium">Address: </span>${(proposalData.address?.line1 || "") +
    "," +
    (proposalData.address?.line2 || "")
    }<br />
                <span class="font-medium">Pin code: </span>${proposalData.pincode || ""
    }<br />
                <span class="font-medium">GST No: </span>${proposalData.gst_number || ""
    }<br />
                <span class="font-medium">Type of Industry: </span>${proposalData.type_of_industry || ""
    }<br />
              </td>
              <td colspan="2">
                <span class="font-medium">Proposal Number: </span>${proposalData.proposal_number || ""
    }<br />
                <span class="font-medium">Proposal Date: </span>${formattedDate}<br />
                <span class="font-medium">Format No: </span><span>AUDIT.REC.AMT.03</span><br />
                <span class="font-medium">Issue Date: </span><span>22.08.2022</span><br />
                <span class="font-medium">Version No: </span><span>1</span><br />
                <span class="font-medium">PO Number: </span>${proposalData.po_number || ""}<br />
              </td>
            </tr>
          </table>
        </div>
        <div class="mt-5 px-4 box-border w-full flex justify-center">
          <table class="border-collapse w-full">
            <tr>
              <th class="px-2 py-1 text-center">Outlet Name</th>
              <th class="px-2 py-1 text-center">Audit Scope & Criteria</th>
              <th class="px-2 py-1 text-center">Criteria for Man day</th>
              ${productionHeader}
              <th class="px-2 py-1 text-center">Man Days</th>
              <th class="px-2 py-1 text-center">Qty</th>
              <th class="px-2 py-1 text-center">Unit Cost</th>
              <th class="px-2 py-1 text-center">Amount</th>
            </tr>
            ${outletRows} 
            ${financialRows}
            <tr>
              <td colspan="${labelColspan}" class="selected-cell border text-right w-3/4 small-cell">
                <strong>Sub Total</strong>
              </td>
              <td class="border w-1/4 selected-cell small-cell text-center "><strong>₹${beforeTaxTotal.toFixed(2)}</strong></td>
            </tr>
           ${taxHtml}
              <td colspan="${labelColspan}" class="border selected-cell text-right w-3/4 small-cell">
                <strong>Total</strong>
              </td>
              <td class="border w-1/4 selected-cell small-cell text-center"><strong>₹${overallTotal.toFixed(2)}</strong></td>
            </tr>
          </table>
        </div>
        <div class="mt-10 px-4">
          
          <div class="w-full flex justify-center">
            <table class="border-collapse w-3/4">
              <tr>
                <td class="w-1/2 border px-4 py-1"><strong>Total Amount (without tax)</strong></td>
                <td class="w-1/2 border px-4 py-1"><strong>₹${beforeTaxTotal.toFixed(2)}</strong></td>
              </tr>
             ${tax2}
                <td class="w-1/2 border px-4 py-1">
                  <strong>Total Amount (With Tax)</strong>
                </td>
                <td class="w-1/2 border px-4 py-1"> <strong>₹${overallTotal.toFixed(2)}</strong></td>
              </tr>
            </table>
          </div>
        </div>
         <div class="mt-5 w-[80%] m-auto">
          <strong>Note:</strong>
          <ol >
            ${noteContent.map((line) => `<li>${line}</li>`).join("")}
           
            ${proposalData.note !== "undefined" && proposalData.note !== ""
      ? `
            <li>4. ${proposalData.note || ""}</li>
            `
      : ""
    }
          </ol>
        </div>
        <div class="mt-10">
          <div class="text-center mb-4">
            <h2 class="font-bold text-sm">Address & Payment Details</h2>
          </div>
           <div class="text-center">
    <table class="border w-4/5 mx-auto">
      <tr>
        <td class="w-1/2 border p-2 align-top">
          <div><strong class="font-medium">Company Name:</strong> ${companyProfile?.company_name || ""}</div>
          <strong class="font-medium">Company Address:</strong>
      
          <div>
           Flat no: F1,First Floor,Door No:519,MM Illam,MKN Road,
Adambakkam Village,Alandur,Chennai. Chennai- 600 016
          </div>
         <div>
  <strong class="font-medium">GST NO:</strong> ${companyProfile?.gstin || ""
    }
</div>


        </td>
        <td class="w-1/2 border p-2 align-top ">
          <strong class="font-medium">Bank Details:</strong>
          <div class="mt-2"> <strong class="font-medium  ">Account Holder:</strong> ${bankDetails?.account_holder_name || "N/A"
    }</div>
          <div class="mt-2"><strong class="font-medium ">Account Number:</strong> ${bankDetails?.account_number || "N/A"
    }</div>
          <div class="mt-2"><strong class="font-medium ">IFSC Code:</strong> ${bankDetails?.ifsc_code || "N/A"
    }</div>
          <div class="mt-2"><strong class="font-medium ">MICR Code:</strong> ${bankDetails?.micr_code || "N/A"
    }</div>
        </td>
      </tr>
    </table>
  </div>
 </div>
</div>
    <div>
    <div class="font-bold text-center mt-[1.5rem]">For Client:</div>
    <div class="mx-auto w-[80%]">
    I hereby accept this offer letter and I commit myself to follow the requirements established including
payment of the licensee fee and the terms and conditions of this proposal
    </div>
    <div class="mt-[1rem] mx-auto w-[80%] space-y-[0.5rem]">
      <div class="flex" >
        <div class="font-bold ">CLIENT REPRESENTATIVE :</div>
        <div class="flex-1 border-black border-b"></div>
      </div>
      <div  class="flex" >
        <div class="font-bold ">DESIGNATION :</div>
        <div class="flex-1 border-black border-b"></div>
      </div>
      <div  class="flex" >
        <div class="font-bold ">SIGNATURE :</div>
        <div class="flex-1 border-black border-b"></div>
      </div>
      <div  class="flex" >
        <div class="font-bold ">DATE :</div>
        <div class="flex-1 border-black border-b"></div>
      </div>
    </div>
    <div>
    </div>
  </div>
  <div class="ml-auto mr-8 w-fit  mt-[2.25rem] space-y-[0.5rem]">
    <div class="flex items-center gap-x-[0.5rem]" ><i class="fa-solid fa-phone"></i><div>+91 88388 70678</div></div>
    <div class="flex items-center gap-x-[0.5rem]" ><i class="fa-solid fa-envelope"></i><div>admin@unavar.com</div></div>
    <div class="flex items-center gap-x-[0.5rem]" ><i class="fa-solid fa-globe"></i><div>www.unavar.com</div></div>
  </div>
  </div>
  <div class="relative"><img src=${LetterHeadBottom} />
  <div class="py-[0.25rem] absolute bottom-0 left-0 right-0 w-full text-white text-center">  <i class="fa-solid fa-location-dot"></i> 
  FLAT F1, FIRST FLOOR, DOOR NO:519, MM ILLAM, MKV ROAD, ADABAKKAM VILLAGE, ALANDUR, CHENNAI-600016
  </div>
  </div>
  </body>
  </html>
  `;

  return (
    <div>
      <AdminDashboard>
        <div className="top-0 z-50 bg-white">
          <div className="mb-10 border shadow-bottom px-4 py-4 flex items-center">
            <span
              onClick={() => navigate(-1)}
              className="cursor-pointer text-3xl mr-4"
            >
              ←
            </span>
            <h2 className="text-xl font-semibold">View Proposal Document</h2>
            <div className="flex ml-auto gap-x-4">
              <Button type="primary" className="ml-auto" onClick={showModal} disabled={!isAddressValid}>
                Edit
              </Button>
              <Button onClick={handleDownload}
                disabled={!isAddressValid || downloading}
                className={`px-4 py-2 rounded-md text-white bg-blue-600 transition-all
                           ${!isAddressValid || downloading
                    ? "opacity-60 cursor-not-allowed blur-[0.5px]"
                    : "hover:bg-blue-700"
                  }`}
              >
                {downloading ? "Downloading..." : "Download PDF"}
              </Button>
            </div>
          </div>
        </div>

        <>
          <div className="fixed bottom-4 right-4 flex items-center space-x-4 z-10">
            <button
              onClick={() =>
                setZoom((prevZoom) => Math.max(prevZoom - 0.1, 0.5))
              }
              className="px-4 py-2 bg-gray-200 border border-gray-400 rounded"
            >
              -
            </button>
            <span className="text-lg">Zoom: {Math.round(zoom * 100)}%</span>
            <button
              onClick={() =>
                setZoom((prevZoom) => Math.min(prevZoom + 0.1, 1.1))
              }
              className="px-4 py-2 bg-gray-200 border border-gray-400 rounded"
            >
              +
            </button>
          </div>

          <div className="flex justify-center">
            <div
              className=""
              style={{
                width: `${793 * zoom}px`, // Adjust width based on zoom
                transform: `scale(${zoom})`,
                transformOrigin: "center", // Center the zoom effect
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: htmlTemplate }} />
            </div>
          </div>
          <UpdateGenerateProposalModal
            visible={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            proposalId={proposalId}
          />
        </>
      </AdminDashboard>
    </div>
  );
};

export default ViewProposal;
